import React from "react";

import ActivityHistoryNudgeIcon from "@common/assets/images/details-pane-icons/nudge/activity-history-nudge.svg";
import IntegrationsNudgeIcon from "@common/assets/images/details-pane-icons/nudge/integrations-nudge.svg";
import MetadataNudgeIcon from "@common/assets/images/details-pane-icons/nudge/metadata-nudge.svg";
import SentryNudgeIcon from "@common/assets/images/details-pane-icons/nudge/sentry-nudge.svg";
import UserDetailsNudgeIcon from "@common/assets/images/details-pane-icons/nudge/user-details-nudge.svg";
import Button from "@common/components/Button/Button";
import { docLink } from "@common/util/docs";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { RoutePaths } from "@main/router/config/routePaths";
import * as Styled from "common/components/Nudge/Nudge.styles";
import { generatePath, useNavigate, useParams } from "react-router-dom";

interface NudgeProps {
  disabled?: boolean;
  tooltip?: string;
}

export const ActivityHistoryNudge = () => {
  return (
    <Styled.ActivityHistoryNudge>
      <NudgeContent
        icon={ActivityHistoryNudgeIcon}
        title={"Activity history"}
        text={"User actions, network traffic, console logs, and other logs leading to this ticket are not available."}
        buttonText={"Learn more"}
        onClick={() => window.open(helpCenterArticles.activityHistoryNotAvailable.url, "_blank")?.focus()}
      />
    </Styled.ActivityHistoryNudge>
  );
};

export const IntegrationsNudge = ({ disabled }: NudgeProps) => {
  const params = useParams<{ userFeedbackKey: string }>();
  const navigate = useNavigate();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  return (
    <Styled.IntegrationsNudge>
      <NudgeContent
        icon={IntegrationsNudgeIcon}
        title={"Ticket integrations"}
        text={
          "Forward tickets to your Jira, Slack, webhooks, or any tool — and see the link right here. No copy-pasting, no wasted time."
        }
        buttonText={"Start forwarding"}
        onClick={() => {
          navigate(
            generatePath(RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK_KEY, {
              ...params,
              workspaceSlug: selectedWorkspace?.slug ?? null,
              appKey: selectedApp?.key ?? null,
            }),
            {
              replace: true,
            },
          );
        }}
        disabled={disabled}
        tooltip={"Sign up to use"}
      />
    </Styled.IntegrationsNudge>
  );
};

export const MetadataNudge = () => {
  const { selectedApp } = useAppSelectionContext();
  const platform = selectedApp?.platform?.name;

  return (
    <Styled.MetadataNudge>
      <NudgeContent
        icon={MetadataNudgeIcon}
        title={"Ticket metadata"}
        text={
          "Automagically attach any custom data (e.g. chat ID, shopping cart items, sync status) from your app — everything will show up right here."
        }
        buttonText={"See Docs"}
        onClick={() => window.open(docLink(platform).addTicketMetadata, "_blank")?.focus()}
      />
    </Styled.MetadataNudge>
  );
};

export const UserDetailsNudge = () => {
  const { selectedApp } = useAppSelectionContext();
  const platform = selectedApp?.platform?.name;

  return (
    <Styled.UserDetailsNudge>
      <NudgeContent
        icon={UserDetailsNudgeIcon}
        title={"User details"}
        text={
          "Register your users to instantly see their attributes here (e.g. name, user ID, email), view their previous tickets, and live chat with them."
        }
        buttonText={"See Docs"}
        onClick={() => window.open(docLink(platform).registerAppUser, "_blank")?.focus()}
      />
    </Styled.UserDetailsNudge>
  );
};

export const SentryNudge = ({ disabled }: NudgeProps) => {
  const navigate = useNavigate();
  const { selectedWorkspace } = useAppSelectionContext();

  return (
    <Styled.SentryNudge>
      <NudgeContent
        icon={SentryNudgeIcon}
        title={"Sentry errors"}
        text={"Display related backend error logs here for easier debugging, without needing to switch between tools."}
        buttonText={"See settings"}
        onClick={() => {
          navigate(
            generatePath(`${RoutePaths.ADMINISTRATION_EXTENSIONS}?type=Sentry`, {
              workspaceSlug: selectedWorkspace?.slug ?? "",
            }),
            {
              replace: true,
            },
          );
        }}
        disabled={disabled}
        tooltip={"Sign up to use"}
      />
    </Styled.SentryNudge>
  );
};

interface NudgeContentProps {
  icon: string;
  title: string;
  text: string;
  buttonText: string;
  onClick: () => void;
  link?: string;
  disabled?: boolean;
  tooltip?: string;
}

const NudgeContent = ({ icon, text, title, buttonText, onClick, disabled, tooltip }: NudgeContentProps) => {
  return (
    <>
      <Styled.NudgeContentDiv>
        <Styled.NudgeTitleDiv>
          <Styled.NudgeTitleIcon src={icon} />
          <Styled.NudgeTitle p1>{title}</Styled.NudgeTitle>
        </Styled.NudgeTitleDiv>
        <Styled.NudgeText p2>{text}</Styled.NudgeText>
      </Styled.NudgeContentDiv>
      <Styled.NudgeButtonDiv>
        <Button
          tooltip={
            tooltip
              ? {
                  position: "top",
                  text: tooltip,
                  disabled: !disabled,
                }
              : undefined
          }
          disabled={disabled}
          onClick={onClick}
        >
          {buttonText}
        </Button>
      </Styled.NudgeButtonDiv>
    </>
  );
};
